import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { BarRaceStage } from '../components/rankings/BarRaceStage';
import { raceDuration } from '../utils/barRace';
import logoIcon from '../assets/logo-icon.png';
import type { RankingTimelineData } from '../types';

/**
 * Server-side capture surface for the evolution video. NOT a user-facing route.
 *
 * Puppeteer injects `window.__EVOLUTION_DATA__` via evaluateOnNewDocument, opens
 * this page at a 540×960 viewport (deviceScaleFactor 2 → 1080×1920), waits for
 * `.capture-ready`, then drives playback frame-by-frame through
 * `window.__seekEvolution(t)` — screenshotting after each seek resolves. It
 * renders the exact same `BarRaceStage` the in-app view uses, so the video is
 * pixel-identical to what members watch.
 */

interface EvolutionCapturePayload {
  timeline: RankingTimelineData;
  rankingName: string;
  rankingImageUrl: string | null;
}

declare global {
  interface Window {
    __EVOLUTION_DATA__?: EvolutionCapturePayload;
    __EVOLUTION_DURATION__?: number;
    __seekEvolution?: (tMs: number) => Promise<void>;
  }
}

const CAPTURE_W = 540;
const CAPTURE_H = 960;

export function RankingEvolutionCapturePage() {
  const [payload] = useState<EvolutionCapturePayload | null>(
    () => window.__EVOLUTION_DATA__ ?? null,
  );
  const [tMs, setTMs] = useState(0);
  const [ready, setReady] = useState(false);

  // Publish total duration so the generator's seek-loop uses the same timing
  // math (barRace.raceDuration) as in-app playback — single source of truth.
  useEffect(() => {
    if (payload) window.__EVOLUTION_DURATION__ = raceDuration(payload.timeline);
  }, [payload]);

  // Expose the frame-perfect seek hook. flushSync commits the DOM synchronously;
  // the double rAF guarantees the browser painted before the screenshot.
  useEffect(() => {
    window.__seekEvolution = (t: number) =>
      new Promise<void>((resolve) => {
        // flushSync commits the DOM synchronously, so the frame is already
        // correct here. The double rAF waits for paint; the setTimeout is a
        // safety net so a throttled/backgrounded tab can never hang the loop.
        flushSync(() => setTMs(t));
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        requestAnimationFrame(() => requestAnimationFrame(finish));
        setTimeout(finish, 100);
      });
    return () => {
      delete window.__seekEvolution;
    };
  }, []);

  // Signal readiness only once fonts + every avatar image have loaded, so the
  // very first frame is already complete (mirrors imageGenerator's wait).
  useEffect(() => {
    if (!payload) {
      setReady(true);
      return;
    }
    let cancelled = false;

    const preloadAvatars = Promise.all(
      payload.timeline.players
        .map((p) => p.avatarUrl)
        .filter((u): u is string => !!u)
        .concat(payload.rankingImageUrl ? [payload.rankingImageUrl] : [])
        .concat([logoIcon]) // brand mark, so it's present in the very first frame
        .map(
          (url) =>
            new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => resolve(); // tolerate broken avatars
              img.src = url;
            }),
        ),
    );

    const fontsReady =
      typeof document !== 'undefined' && document.fonts
        ? document.fonts.ready
        : Promise.resolve();

    Promise.all([preloadAvatars, fontsReady]).then(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [payload]);

  return (
    <div
      className="bg-deep-blue overflow-hidden"
      style={{ width: CAPTURE_W, height: CAPTURE_H, position: 'fixed', top: 0, left: 0 }}
    >
      {payload && (
        <BarRaceStage
          timeline={payload.timeline}
          tMs={tMs}
          rankingName={payload.rankingName}
          rankingImageUrl={payload.rankingImageUrl}
          rowHeight={56}
          showChrome
        />
      )}
      {ready && <div className="capture-ready" style={{ position: 'absolute', width: 0, height: 0 }} />}
    </div>
  );
}
